import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, Loader2, X } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import PageTilesGrid from "../components/PageTilesGrid";
import { getPosts, resolveAsset } from "../lib/api";
import { hasPostBody } from "../lib/posts";
import { usePostTypes, labelForType } from "../lib/postTypes";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";
import CardIntro from "../components/CardIntro";

const typeKicker = { long: "Long read", short: "Short note", note: "Note" };

export default function BlastPage() {
  const copy = usePageContent("retro");
  const { types: postTypes } = usePostTypes();
  const [posts, setPosts] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images: [...], index }

  useEffect(() => {
    let alive = true;
    getPosts("past")
      .then((p) => alive && setPosts(p))
      .catch(() => alive && setPosts([]));
    return () => {
      alive = false;
    };
  }, []);

  // Flatten all images across posts for the gallery strip
  const allImages = (posts || []).flatMap((p) => {
    const imgs = [...(p.images || [])];
    if (p.cover_image && !imgs.includes(p.cover_image)) imgs.unshift(p.cover_image);
    return imgs.map((url) => ({ url, post: p }));
  });

  useEffect(() => {
    const onKey = (e) => {
      if (!lightbox) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight")
        setLightbox((l) =>
          l ? { ...l, index: Math.min(l.index + 1, l.images.length - 1) } : null
        );
      if (e.key === "ArrowLeft")
        setLightbox((l) => (l ? { ...l, index: Math.max(l.index - 1, 0) } : null));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div
      className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]"
      data-testid="page-blast"
    >
      <Header />

      <section
        className="mx-auto max-w-7xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10 sm:pb-20"
        data-testid="blast-hero"
      >
        {copy.hide_header ? null : (
          <div>
            <p className="label-kicker text-brand-accent" style={kickerStyle(copy)}>
              {copy.kicker}
            </p>
            <h1
              className="brand-headline mt-3 whitespace-pre-line font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl"
              style={titleStyle(copy)}
            >
              {copy.title}
            </h1>
            {copy.intro ? (
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-[var(--brand-body)]">
                {copy.intro}
              </p>
            ) : null}
          </div>
        )}

        {/* Posts list */}
        {posts === null ? (
          <div
            className="mt-6 flex items-center gap-2 text-[var(--brand-body)]"
            data-testid="blast-loading"
          >
            <Loader2 size={16} className="animate-spin" /> Loading the archive…
          </div>
        ) : posts.length === 0 ? (
          (copy.empty_title || copy.empty_body) ? (
            <div
              className="mt-6 rounded-md border border-dashed border-black/15 p-12 text-center"
              data-testid="blast-empty"
            >
              {copy.empty_title ? (
                <p className="font-display text-lg font-bold">{copy.empty_title}</p>
              ) : null}
              {copy.empty_body ? (
                <p className="mt-2 text-sm text-[var(--brand-body)]">{copy.empty_body}</p>
              ) : null}
            </div>
          ) : null
        ) : (
          <div
            className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2"
            data-testid="blast-posts"
          >
            {posts.map((p) => (
              <BlastCard key={p.id} post={p} postTypes={postTypes} />
            ))}
          </div>
        )}

        {/* Combined gallery */}
        {allImages.length > 0 ? (
          <div className="mt-20 border-t border-black/10 pt-10" data-testid="blast-gallery">
            <div className="mb-6 flex items-center gap-2 text-[#6CABDD]">
              <Camera size={14} strokeWidth={2.5} />
              <span className="label-kicker">Gallery</span>
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight sm:text-3xl">
              Every snapshot from the archive.
            </h2>
            <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
              Click any photo to open the full view. Arrow keys to navigate.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {allImages.map((img, idx) => (
                <button
                  key={`${img.url}-${idx}`}
                  type="button"
                  onClick={() =>
                    setLightbox({
                      images: allImages.map((x) => x.url),
                      index: idx,
                    })
                  }
                  data-testid="blast-gallery-thumb"
                  className="group relative aspect-square overflow-hidden rounded-md border border-black/10 bg-[#F8FAFC]"
                >
                  <img
                    src={resolveAsset(img.url)}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-[#0A0F17]/0 transition-colors group-hover:bg-[#0A0F17]/10" />
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-8 sm:pb-20">
        <PageTilesGrid pageKey="retro" kicker="" heading="" />
      </section>

      {lightbox ? (
        <Lightbox
          urls={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onPrev={() =>
            setLightbox((l) =>
              l ? { ...l, index: Math.max(l.index - 1, 0) } : null
            )
          }
          onNext={() =>
            setLightbox((l) =>
              l ? { ...l, index: Math.min(l.index + 1, l.images.length - 1) } : null
            )
          }
        />
      ) : null}

      <Footer />
    </div>
  );
}

const BlastCard = ({ post, postTypes }) => {
  const cover = post.cover_image || post.images?.[0] || null;
  const label = labelForType(post.post_type, postTypes) || typeKicker[post.post_type] || "Post";
  return (
    <div
      data-testid="blast-card"
      className="card-hover group relative flex flex-col overflow-hidden rounded-md border border-black/10 bg-white"
    >
      {/* Stretched link — sits absolute over the whole card so clicking
          anywhere navigates to the post. Inline links inside the
          card-intro override this via `.card-intro a` (z-index in
          index.css). Rendered first so anchors elsewhere paint on top. */}
      <Link
        to={`/post/${post.slug || post.id}`}
        aria-label={post.title}
        className="absolute inset-0 z-[1]"
        data-testid="blast-card-stretched-link"
      />
      {cover ? (
        <div className="pointer-events-none relative z-[2] aspect-[16/9] w-full overflow-hidden bg-[#F8FAFC]">
          <img
            src={resolveAsset(cover)}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            style={{
              objectPosition: `${post.cover_focal_x ?? 50}% ${post.cover_focal_y ?? 50}%`,
            }}
          />
        </div>
      ) : (
        <div className="pointer-events-none relative z-[2] h-2 w-full bg-[#6CABDD]" />
      )}
      <div className="pointer-events-none relative z-[2] flex flex-1 flex-col gap-3 p-6">
        <div className="flex items-center gap-3">
          <span className="rounded-sm bg-[#6CABDD] px-2 py-1 label-kicker text-white">
            {label}
          </span>
          <span className="label-kicker text-[var(--brand-body)]">
            {new Date(post.display_at || post.created_at).toLocaleDateString("en-GB", {
              day: "numeric", month: "short", year: "numeric",
            })}
          </span>
          {post.images?.length ? (
            <span className="label-kicker text-[var(--brand-body)]">
              · {post.images.length} photo{post.images.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <h3 className="font-display text-2xl font-black leading-tight tracking-tight">
          {post.title}
        </h3>
        <CardIntro
          post={post}
          maxLen={320}
          className="line-clamp-3 text-sm leading-relaxed text-[var(--brand-body)]"
          data-testid="blast-card-intro"
        />
        {hasPostBody(post) ? (
          <span
            data-testid="blast-card-read-cta"
            className="mt-auto inline-flex items-center gap-1.5 font-display text-sm font-bold text-[var(--brand-ink)]"
          >
            Read the memory
            <ArrowUpRight
              size={14}
              className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </span>
        ) : null}
      </div>
    </div>
  );
};

const Lightbox = ({ urls, index, onClose, onPrev, onNext }) => {
  const current = urls[index];
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      data-testid="blast-lightbox"
    >
      <button
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[var(--brand-ink)]"
        onClick={onClose}
        aria-label="Close gallery"
        data-testid="blast-lightbox-close"
      >
        <X size={18} />
      </button>
      <button
        className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 px-3 py-2 label-kicker text-white hover:bg-white/20 disabled:opacity-30"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        disabled={index === 0}
      >
        ← Prev
      </button>
      <button
        className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 px-3 py-2 label-kicker text-white hover:bg-white/20 disabled:opacity-30"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        disabled={index === urls.length - 1}
      >
        Next →
      </button>
      <img
        src={resolveAsset(current)}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[90vw] rounded-md object-contain shadow-2xl"
        data-testid="blast-lightbox-image"
      />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 label-kicker text-white/70">
        {index + 1} / {urls.length}
      </div>
    </div>
  );
};
